const TIMEZONE_KEY = 'timezone';

/**
 * Gets the raw timezone string from session storage
 * @returns {string} The timezone string (e.g., 'America/New_York') or the local timezone if not set
 */
export const getTimezone = () => {
    const storedTimezone = localStorage.getItem(TIMEZONE_KEY);
    if (!storedTimezone) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        localStorage.setItem(TIMEZONE_KEY, timezone);
        return timezone;
    }

    return storedTimezone;
};


/**
 * Gets the timezone object for the current or specified timezone
 * @param {string} [timezone] - Optional timezone string. If not provided, uses the stored timezone
 * @returns {Object} Timezone information object
 */
export const getTimezoneInfo = (timezone = getTimezone()) => {
    const date = new Date();
    const options = {
        timeZone: timezone,
        timeZoneName: 'long',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric'
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');

    return {
        id: timezone,
        displayName: timeZonePart ? timeZonePart.value : timezone,
        offset: getTimezoneOffset(timezone)
    };
};

/**
 * Gets the timezone offset in minutes for the specified timezone
 * @param {string} [timezone] - Optional timezone string. If not provided, uses the stored timezone
 * @returns {number} Timezone offset in minutes
 */
export const getTimezoneOffset = (timezone = getTimezone()) => {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    return (utcDate - tzDate) / 60000; // Convert milliseconds to minutes
};

/**
 * Formats a date to the specified timezone
 * @param {Date|string} date - The date to format
 * @param {string} [timezone] - Optional timezone string. If not provided, uses the stored timezone
 * @param {Object} [options] - Optional Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatInTimezone = (date, timezone = getTimezone(), options = {}) => {
    const defaultOptions = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        hour12: true
    };

    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(date));
};

/**
 * Converts a date to the specified timezone
 * @param {Date|string} date - The date to convert
 * @param {string} [timezone] - Optional timezone string. If not provided, uses the stored timezone
 * @returns {Date} Date object in the specified timezone
 */
export const convertToTimezone = (date, timezone = getTimezone()) => {
    const targetDate = new Date(date);
    const targetOffset = getTimezoneOffset(timezone);
    const localOffset = new Date().getTimezoneOffset();
    const offsetDiff = localOffset - targetOffset;
    
    return new Date(targetDate.getTime() + offsetDiff * 60000);
};